/**
 * StateResetUtility - Utility for resetting worker client state
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for resetting various state components
 * of the worker client, such as caches, flags, and document state.
 */

/**
 * Options for state reset operations
 */
export interface StateResetOptions {
  clearPageCache?: boolean;
  clearSentFonts?: boolean;
  clearTelemetry?: boolean;
  clearCancelledLayouts?: boolean;
  resetDocHash?: boolean;
  resetDocumentInitialized?: boolean;
  resetIsReady?: boolean;
}

/**
 * Reset worker client state
 * 
 * Resets various state components based on the provided options.
 * This is useful for error recovery, reinitialization, or cleanup scenarios.
 * 
 * @param state - Object containing state properties to reset
 * @param options - Options specifying which state components to reset
 * @returns Object with reset state values
 */
export function resetWorkerState(
  state: {
    pageCache?: Map<any, any>;
    sentFonts?: Set<any>;
    telemetryData?: Map<any, any>;
    cancelledLayoutIds?: Set<any>;
    currentDocHash?: string;
    documentInitialized?: boolean;
    isReady?: boolean;
  },
  options: StateResetOptions = {}
): {
  pageCacheCleared: boolean;
  sentFontsCleared: boolean;
  telemetryCleared: boolean;
  cancelledLayoutsCleared: boolean;
  docHashReset: boolean;
  documentInitializedReset: boolean;
  isReadyReset: boolean;
} {
  const {
    clearPageCache = false,
    clearSentFonts = false,
    clearTelemetry = false,
    clearCancelledLayouts = false,
    resetDocHash = false,
    resetDocumentInitialized = false,
    resetIsReady = false
  } = options;

  const result = {
    pageCacheCleared: false,
    sentFontsCleared: false,
    telemetryCleared: false,
    cancelledLayoutsCleared: false,
    docHashReset: false,
    documentInitializedReset: false,
    isReadyReset: false
  };

  if (clearPageCache && state.pageCache) {
    state.pageCache.clear();
    result.pageCacheCleared = true;
  }

  if (clearSentFonts && state.sentFonts) {
    state.sentFonts.clear();
    result.sentFontsCleared = true;
  }

  if (clearTelemetry && state.telemetryData) {
    state.telemetryData.clear();
    result.telemetryCleared = true;
  }

  if (clearCancelledLayouts && state.cancelledLayoutIds) {
    state.cancelledLayoutIds.clear();
    result.cancelledLayoutsCleared = true;
  }

  if (resetDocHash && state.currentDocHash !== undefined) {
    state.currentDocHash = '';
    result.docHashReset = true;
  }

  if (resetDocumentInitialized && state.documentInitialized !== undefined) {
    state.documentInitialized = false;
    result.documentInitializedReset = true;
  }

  if (resetIsReady && state.isReady !== undefined) {
    state.isReady = false;
    result.isReadyReset = true;
  }

  return result;
}

/**
 * Reset all worker state (full reset)
 * 
 * Convenience function that resets all state components.
 * 
 * @param state - Object containing state properties to reset
 * @returns Object with reset state values
 */
export function resetAllWorkerState(state: {
  pageCache?: Map<any, any>;
  sentFonts?: Set<any>;
  telemetryData?: Map<any, any>;
  cancelledLayoutIds?: Set<any>;
  currentDocHash?: string;
  documentInitialized?: boolean;
  isReady?: boolean;
}): ReturnType<typeof resetWorkerState> {
  return resetWorkerState(state, {
    clearPageCache: true,
    clearSentFonts: true,
    clearTelemetry: true,
    clearCancelledLayouts: true,
    resetDocHash: true,
    resetDocumentInitialized: true,
    resetIsReady: true
  });
}

/**
 * Reset state for error recovery
 * 
 * Resets state components typically needed after a worker error.
 * 
 * @param state - Object containing state properties to reset
 * @returns Object with reset state values
 */
export function resetStateForErrorRecovery(state: {
  pageCache?: Map<any, any>;
  sentFonts?: Set<any>;
  currentDocHash?: string;
  documentInitialized?: boolean;
  isReady?: boolean;
}): ReturnType<typeof resetWorkerState> {
  return resetWorkerState(state, {
    clearPageCache: true,
    clearSentFonts: true,
    resetDocHash: true,
    resetDocumentInitialized: true,
    resetIsReady: true
  });
}

/**
 * Reset state for document reinitialization
 * 
 * Resets state components needed when forcing document reinitialization.
 * 
 * @param state - Object containing state properties to reset
 * @returns Object with reset state values
 */
export function resetStateForReinitialization(state: {
  pageCache?: Map<any, any>;
  sentFonts?: Set<any>;
  currentDocHash?: string;
}): ReturnType<typeof resetWorkerState> {
  return resetWorkerState(state, {
    clearPageCache: true,
    clearSentFonts: true,
    resetDocHash: true
  });
}

